/**
 * Cause Report Constants
 */

import { AlertTriangle, HeartCrack, Frown } from 'lucide-react';
import type { ConductDimensionId } from '../types';
import React from "react";

/** Query keys for React Query */
export const causeReportKeys = {
  all: ['cause-report'] as const,
  overview: (datasetId: string, filters: Record<string, unknown>) =>
    [...causeReportKeys.all, 'overview', datasetId, filters] as const,
  categories: (datasetId: string, filters: Record<string, unknown>) =>
    [...causeReportKeys.all, 'categories', datasetId, filters] as const,
  foundation: (datasetId: string, filters: Record<string, unknown>) =>
    [...causeReportKeys.all, 'foundation', datasetId, filters] as const,
};

/** Stale time for queries (60 seconds) */
export const CAUSE_REPORT_STALE_TIME = 60 * 1000;

/** Treemap color palette */
export const TREEMAP_COLORS = [
  '#5B2D8E',  // Dark purple
  '#7E57C2',  // Medium purple
  '#E8A0BF',  // Light pink/lavender
  '#00897B',  // Dark teal
  '#00B8D4',  // Bright cyan
  '#1A1A4E',  // Dark navy
  '#8B1A2B',  // Dark maroon
  '#E53935',  // Coral red
  '#4DB6AC',  // Medium teal
  '#3949AB',  // Medium indigo
] as const;

/** Default color for categories beyond palette */
export const TREEMAP_DEFAULT_COLOR = '#6B7280';

/** Delta formatting */
export const DELTA_THRESHOLDS = {
  significant: 10,  // Show as significant change
  neutral: 2,       // Within neutral range
} as const;

// =============================================================================
// Top Causes Constants
// =============================================================================

/** Query keys for top causes */
export const topCausesKeys = {
  all: [...causeReportKeys.all, 'top-causes'] as const,
  list: (
    datasetId: string,
    filters: Record<string, unknown>,
    categoryFilter?: string
  ) => [...topCausesKeys.all, 'list', datasetId, filters, categoryFilter] as const,
};

/** Number of top causes to display */
export const TOP_CAUSES_LIMIT = 10;

// =============================================================================
// Consumer Duty Outcomes Constants
// =============================================================================

/** Query keys for outcomes */
export const outcomesKeys = {
  all: [...causeReportKeys.all, 'outcomes'] as const,
  list: (datasetId: string, filters: Record<string, unknown>) =>
    [...outcomesKeys.all, 'list', datasetId, filters] as const,
  records: (datasetId: string, outcomeId: string, filters: Record<string, unknown>) =>
    [...outcomesKeys.all, 'records', datasetId, outcomeId, filters] as const,
  labels: (datasetId: string, outcomeId: string, filters: Record<string, unknown>, sortBy?: string) =>
    [...outcomesKeys.all, 'labels', datasetId, outcomeId, filters, sortBy] as const,
  trends: (datasetId: string, outcomeId: string, filters: Record<string, unknown>, aggregation?: string) =>
    [...outcomesKeys.all, 'trends', datasetId, outcomeId, filters, aggregation] as const,
  positiveLabels: (datasetId: string, outcomeId: string, filters: Record<string, unknown>) =>
    [...outcomesKeys.all, 'positive-labels', datasetId, outcomeId, filters] as const,
};

/** Number of records per page in dialog record tables */
export const DIALOG_RECORDS_PAGE_SIZE = 10;

/** Consumer Duty outcome colors — aligned with outcomes_report prototype palette */
export const CONSUMER_DUTY_OUTCOMES = {
  consumer_support: { color: '#0003AE' },       // Sapphire (prototype --sapphire)
  consumer_understanding: { color: '#924EFF' }, // Purple (prototype --purple)
  price_and_fair_value: { color: '#92204D' },   // Berry (prototype --berry)
  product_design: { color: '#07A0C3' },         // Teal (prototype --teal)
} as const;

// =============================================================================
// Advanced Analysis Constants
// =============================================================================

/** Query keys for dimensions */
export const dimensionsKeys = {
  all: [...causeReportKeys.all, 'dimensions'] as const,
  list: (datasetId: string, filters: Record<string, unknown>) =>
    [...dimensionsKeys.all, 'list', datasetId, filters] as const,
};

/** Query keys for high-risk records */
export const highRiskKeys = {
  all: [...causeReportKeys.all, 'high-risk'] as const,
  list: (datasetId: string, filters: Record<string, unknown>) =>
    [...highRiskKeys.all, 'list', datasetId, filters] as const,
};

/** Conduct dimension colors and display names */
export const CONDUCT_DIMENSIONS: Record<ConductDimensionId, { color: string; displayName: string }> = {
  foreseeable_harm: { color: '#EF4444', displayName: 'Foreseeable Harm' },
  customer_strain: { color: '#8B5CF6', displayName: 'Customer Strain' },
  dissatisfaction: { color: '#F59E0B', displayName: 'Dissatisfaction' },
};

/** Icons for each conduct dimension */
export const DIMENSION_ICONS: Record<ConductDimensionId, React.ComponentType<{ className?: string }>> = {
  foreseeable_harm: AlertTriangle,
  customer_strain: HeartCrack,
  dissatisfaction: Frown,
};

/** Number of high-risk records to show */
export const HIGH_RISK_RECORDS_LIMIT = 5;

/** Conduct score threshold for high-risk */
export const HIGH_RISK_THRESHOLD = 7;

// =============================================================================
// PDF Export Constants (frontend-only polling settings)
// =============================================================================

/** Default export sections */
export const DEFAULT_EXPORT_SECTIONS = {
  overview: true,
  categories: true,
  topCauses: true,
  outcomes: true,
  dimensions: true,
  highRisk: true,
} as const;

/** Export polling interval (ms) - frontend-only setting for status polling */
export const EXPORT_POLL_INTERVAL = 2000;

/** Export timeout (ms) - frontend-only setting for max polling duration */
export const EXPORT_TIMEOUT = 120000;  // 2 minutes

/**
 * Note: Backend export expiry is configured via CAUSE_REPORT_EXPORT_EXPIRY_HOURS
 * and HIGH_RISK_THRESHOLD is configured via CAUSE_REPORT_HIGH_RISK_THRESHOLD.
 * Frontend uses HIGH_RISK_THRESHOLD for display only; the API defaults to the config value.
 */
